import makeStyles from "@mui/styles/makeStyles";
import { styled, Theme } from "@mui/material/styles";
import { colors } from "@zapalacorp/tokens";
import { FormControl, TextField, TextFieldProps, TextFieldVariants } from "@mui/material";
import { getBrandCustom } from "Util/getCustomColor";

type TextFieldSuperProps = {
  variant?: TextFieldVariants;
} & Omit<TextFieldProps, "variant">;

export interface TextFieldCustomizedProps extends TextFieldSuperProps {
  disabled?: boolean;
  multiline?: boolean;
  error?: boolean;
  bordercolor?: string;
}

export const FormControlCustomized = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== "bordercolor" && prop !== "disabled",
})<{ bordercolor?: string; disabled?: boolean }>(({ bordercolor, disabled }) => ({
  display: "flex",
  justifyContent: "flex-end",
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: !disabled ? bordercolor : "#C4C4C4",
  },
  "&:active .MuiOutlinedInput-notchedOutline": {
    borderColor: !disabled ? bordercolor : "#C4C4C4",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: !disabled ? bordercolor : "#C4C4C4",
  },
}));

export const WrapperInfo = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  cursor: "help",
  "& > svg": {
    color: colors.gray500,
    fontSize: 14,
    fontWeight: 600,
  },
}));

export const WrapperLabel = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
}));

interface StyledProps {
  disabled?: boolean;
  bordercolor?: string;
  error?: boolean;
  multiline?: boolean;
}

export const TextFieldCustomized = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "bordercolor" && prop !== "error" && prop !== "multiline",
})<StyledProps>(({ disabled, bordercolor, error, multiline, theme }) => ({
  "& .MuiInputBase-input:disabled": {
    color: "#98A2B3",
  },
  "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active":
    {
      WebkitBoxShadow: "0 0 0 30px white inset !important",
      WebkitTextFillColor: "#667085",
    },
  "& .MuiOutlinedInput-root": {
    backgroundColor: disabled ? "#F9FAFB" : "#FFFFFF",
    height: multiline ? undefined : "40px",
    borderRadius: "8px",
    fontFamily: "Manrope",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "24px",
    color: "#667085",

    "& fieldset": {
      borderColor: error ? bordercolor : "#C4C4C4",
    },
    "&:hover fieldset": {
      borderColor: !disabled ? bordercolor : "#C4C4C4",
    },
    "&.Mui-focused fieldset": {
      borderColor: !disabled ? bordercolor : "#C4C4C4",
    },

    ".MuiInputAdornment-root": {
      color: "#C4C4C4",
    },
    "&.Mui-focused .MuiInputAdornment-root": {
      color: colors.brandBlue600,
    },
    "&.Mui-error .MuiInputAdornment-root": {
      color: colors.error600,
    },
    "&.Mui-disabled .MuiInputAdornment-root": {
      color: "#C4C4C4",
    },

    "input::-ms-reveal": {
      display: "none",
    },
    "input::-ms-clear": {
      display: "none",
    },
  },
}));

interface StyleProps {
  disabled?: boolean;
}

export const styles = makeStyles<Theme, StyleProps>(() => ({
  iconError: {
    marginRight: "5px",
    fontSize: "16px",
  },
  extraSmallIcon: {
    fontSize: "14px",
  },
  keyboardDatepickerIcon: {
    width: "14px",
    height: "14px",
  },
  formHelper: {
    fontFamily: "Manrope",
    fontStyle: "normal",
    fontWeight: "normal",
    fontSize: "14px",
    lineHeight: "24px",
    color: "#D92D20",
    alignItems: "center",
    display: "flex",
    margin: 0,
  },
  label: {
    fontFamily: "Manrope",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "24px",
    color: (props: StyleProps) =>
      props.disabled ? "#667085" : getBrandCustom("darkPrimaryColor", colors.brandBlue600),
    marginBottom: "4px",
  },
  required: {
    color: (props: StyleProps) => (props.disabled ? "#667085" : "#DC3545"),
  },
}));

export default styles;
